// @givmo/employer-module — WHERE the module's pages live in the host that renders them (EP-MNT4).
//
// ══ ONE PACKAGE, TWO ADDRESS BOOKS ═════════════════════════════════════════════════════════════
//
// The same pages render in two hosts: the employer portal (`apps/admin`), where they sit at the
// portal's root (`/dashboard`, `/approvals`, …), and the company console (`apps/company`), where they
// are the Donations module mounted at `/donations` (`/donations`, `/donations/approvals`, …). A page
// that navigates — a "See all approvals" link, a competition's detail, the onboarding redirect — must
// land on THIS host's address, so every in-module address and every door OUT of the module is read
// from here rather than spelled at the call site.
//
// The DEFAULT is the portal's book, byte for byte: a host that supplies nothing (`apps/admin` supplies
// nothing) gets exactly the addresses these pages carried before this seam existed — its routes,
// testids and copy unchanged (EP-MNT3's byte-identity law holds). The console wraps the mounted pages
// in `EmployerModulePathsProvider` with `DONATIONS_PATHS`.
//
// ══ DOORS OUT OF THE MODULE CARRY THEIR WORDS ══════════════════════════════════════════════════
//
// Three links leave the module — to where the company's cards and wallet live, to the join-request
// inbox, and to the tax documents. Those places are different PAGES in the two hosts (the portal's
// Settings tab and Admin Team; the console's Organization › Wallet and Join requests), so a door
// carries the link text that names its destination beside the address: a link that says "Settings →
// Payment Methods" and lands on the Wallet is a control that lies about where it goes.
//
// ══ SENTENCES THAT NAME A PLACE OR A ROLE CARRY THEIR WORDS TOO (EP-MNT5(h)) ═══════════════════
//
// A sentence that tells someone where to go — "add funds in Settings → Payment Methods", "add your
// headcount in Settings → Account info" — or who can act — "an Owner can add funds" — names a portal
// place and a portal role. In the console those places are the Wallet and Profile pages. The one who
// can add funds is an Owner there too since EP-SUITE7 (D-EP-50): the deposit string sits in the
// Donations module's Owner catalog, so its Owners hold it by default and every Global Admin by tier.
// The receipt fallback is the exception, because it is the backend's choice, not a permission: the
// base-role Owner, whom the console calls a Global Admin. So each such sentence is the host's
// (`words`): the portal's byte for byte, the console's its own.
//
// ══ THE CONTEXTUAL PRIVACY DOOR (EP-MNT5(c), D-EP-48 ruling 3) ═════════════════════════════════════
//
// "Settings live where users look for them; links meet them where the need occurs": the console's
// Donations dashboard carries a quiet link onto the Company visibility settings — the
// composed Settings page, opened as from the dashboard, at the card's anchor. The portal keeps its
// Privacy tab where it has always been, so its book has no such door (`privacy: null`).
//
// ══ THE AI REQUESTS DOOR (MCP-P6a; floors design decision 4.1) ══════════════════════════════════
//
// The Dashboard's "Needs attention" carries a line while AI requests wait for this person, a door
// onto the console's inbox. The host supplies it whole — the address, its name, and how many wait
// (`useWaiting`, the nav's `countBadge.useCount` shape), because only the host knows its organization
// and whether its backend serves the inbox at all. The portal has no inbox (`aiRequests: null`), and
// neither does the console's static book: the console composes its door in (`donations-mount.tsx`).

import { createContext, use, type ReactNode } from 'react';

/** A link OUT of the module: where it goes, and the words that name that place in this host. */
export interface EmployerModuleDoor {
  /** The in-app path. */
  to: string;
  /** Search params the destination needs (the portal's Settings tab id). */
  search?: Readonly<Record<string, string>>;
  /** The link's text — the destination's name in this host. */
  label: string;
}

/**
 * The sentences in the module's pages that name a place or a role differing between the hosts
 * (EP-MNT5(h)). The portal's are its words byte for byte; the console's name its own pages and roles.
 */
export interface EmployerModuleWords {
  /** The approve-match dialog with no saved card on file. */
  approveNoCard: string;
  /** A competition the wallet cannot hold — for a caller who can add funds. */
  publishNoFundsSelf: string;
  /** …for a caller who cannot. */
  publishNoFundsOther: string;
  /** A gift-card compose line after the wallet's shortfall: who adds funds, where — or a card. */
  giftCardAddFunds: string;
  /** A gift-card send refused for funds: the sentence after the shortfall. */
  giftCardAddFundsRetry: string;
  /** The Dashboard's Participation prompt when no headcount is declared. */
  headcountPromptDashboard: string;
  /** The Invites tab's enrollment prompt when no headcount is declared. */
  headcountPromptInvites: string;
  /**
   * The enrollment methodology's clause on where the declared headcount is edited, with its leading
   * comma — or empty where the host names no such place.
   */
  headcountEditable: string;
  /** The enrollment methodology's sentence when no headcount is declared. */
  headcountAddWhere: string;
  /** The Dashboard's wallet stat for a caller who cannot add funds: who can. */
  balanceAskDepositor: string;
  /**
   * Who a card-funded occasion program's receipt goes to when no admin is named — the backend's
   * fallback, the company's top tier — as the programs list names it…
   */
  receiptFallbackRecipient: string;
  /** …as the program dialog's recipient picker offers it… */
  receiptFallbackOption: string;
  /**
   * …and the dialog's note on what happens when the named admin has left. The backend re-resolves the
   * designee every time a card-funded program charges; saying so at authoring time is what tells the
   * admin a departure is handled rather than silently undeliverable.
   */
  receiptFallbackNote: string;
}

/**
 * A door onto a host inbox that also says how many of its items wait for THIS person. `useWaiting`
 * is a hook, called once on every render of the block that draws the door: `null` while the count is
 * unknown or the inbox is not served here (a dark environment's 404), and the line draws nothing.
 * `atLeast` says the count may not be the total.
 */
export interface EmployerModuleWaitingDoor extends EmployerModuleDoor {
  useWaiting: () => { count: number; atLeast: boolean } | null;
}

/** A door onto a place in this host that opens at an anchor — the contextual privacy door. */
export interface EmployerModuleAnchoredDoor extends EmployerModuleDoor {
  /** The card's `id` on the destination page. */
  hash: string;
}

/** Every address the module's pages navigate to, in one host. */
export interface EmployerModulePaths {
  /** The module's landing — the Dashboard. */
  dashboard: string;
  approvals: string;
  give: string;
  matchProgram: string;
  giftCards: string;
  employees: string;
  competitions: string;
  competitionNew: string;
  /** A route TEMPLATE (`$competitionId`), handed to the router with `params`. */
  competitionDetail: string;
  /** A route TEMPLATE (`$competitionId`), handed to the router with `params`. */
  competitionEdit: string;
  reports: string;
  /** The first-run match-policy wizard. */
  onboarding: string;
  /** Where an Owner adds funds or a card — a door OUT of the module. */
  paymentMethods: EmployerModuleDoor & {
    /** A button's words for the same door ("Go to …"). */
    goLabel: string;
  };
  /** Where pending join requests are decided — a door OUT of the module. */
  joinRequests: EmployerModuleDoor & {
    /** The Needs-attention row's one-line instruction, naming the same place. */
    detail: string;
  };
  /**
   * Where the annual-report PDFs live when they are NOT a section of the Reports page: `null` in the
   * portal (the Tax documents section renders inline), a door in the console (the Organization's own
   * tax-documents page, EP-MNT4 — the Reports split).
   */
  taxDocuments: EmployerModuleDoor | null;
  /**
   * The contextual door onto the Company visibility settings, shown on the Dashboard to a
   * holder of the settings' write string — `null` where the host keeps them one click away already
   * (the portal's Settings → Privacy tab).
   */
  privacy: EmployerModuleAnchoredDoor | null;
  /**
   * The organization's AI requests inbox (MCP-P6a), drawn as a "Needs attention" line while requests
   * wait for this person — or `null` where the host has none (the portal; the console's static book).
   */
  aiRequests: EmployerModuleWaitingDoor | null;
  /** The sentences that name a place or a role in this host. */
  words: EmployerModuleWords;
}

/** The portal's words — byte for byte the sentences these pages carried before EP-MNT5. */
export const PORTAL_WORDS: EmployerModuleWords = {
  approveNoCard:
    'You need a saved company card to approve a match. Add one under Settings → Payment Methods, then ' +
    'come back to approve.',
  publishNoFundsSelf:
    'Your company wallet does not have enough available balance to hold this prize. Add funds in ' +
    'Settings → Payment Methods, then publish again.',
  publishNoFundsOther:
    'Your company wallet does not have enough available balance to hold this prize. Only a company ' +
    'Owner can add funds — ask an Owner to top up the wallet in Settings → Payment Methods, then ' +
    'publish again.',
  giftCardAddFunds:
    'an Owner can add funds in Settings → Payment Methods, or pay with a company card.',
  giftCardAddFundsRetry:
    'An Owner can add funds in Settings → Payment Methods, or pay with a company card, then try again.',
  headcountPromptDashboard:
    'Add your employee headcount in Settings to see what share of your company has joined.',
  headcountPromptInvites:
    'Add your employee headcount in Settings → Account info to see what share of your company has ' +
    'joined.',
  headcountEditable: ', editable in Settings (Account info)',
  headcountAddWhere: 'Add your headcount in Settings to see a rate.',
  balanceAskDepositor: 'Ask an Owner to add funds',
  receiptFallbackRecipient: 'An Owner',
  receiptFallbackOption: 'Default (an Owner)',
  receiptFallbackNote:
    'If this person is no longer a company admin when a gift goes out, the receipt goes to an Owner ' +
    'instead — it is never left undeliverable.',
};

/**
 * The console's words: the Wallet and Profile pages by name, and an Owner as the person who adds funds
 * — since EP-SUITE7 (D-EP-50) the deposit string is in the Donations module's Owner catalog, so its
 * Owners hold it by default and a Global Admin by tier, as the portal's words already said. The receipt
 * fallback names a Global Admin: the backend falls back to the company's base-role Owner
 * (`is_company_owner`), whom the console calls a Global Admin (its "Owner" is a module's owner, who is
 * not that fallback).
 */
export const DONATIONS_WORDS: EmployerModuleWords = {
  approveNoCard:
    'You need a saved company card to approve a match. Add one on the Wallet page, then come back ' +
    'to approve.',
  publishNoFundsSelf:
    'Your company wallet does not have enough available balance to hold this prize. Add funds on ' +
    'the Wallet page, then publish again.',
  publishNoFundsOther:
    'Your company wallet does not have enough available balance to hold this prize. Ask an Owner to ' +
    'top up the wallet on the Wallet page, then publish again.',
  giftCardAddFunds: 'an Owner can add funds on the Wallet page, or pay with a company card.',
  giftCardAddFundsRetry:
    'An Owner can add funds on the Wallet page, or pay with a company card, then try again.',
  headcountPromptDashboard:
    'Add your employee headcount on the Profile page to see what share of your company has joined.',
  headcountPromptInvites:
    'Add your employee headcount on the Profile page to see what share of your company has joined.',
  headcountEditable: ', editable on the Profile page',
  headcountAddWhere: 'Add your headcount on the Profile page to see a rate.',
  balanceAskDepositor: 'Ask an Owner to add funds',
  receiptFallbackRecipient: 'A Global Admin',
  receiptFallbackOption: 'Default (a Global Admin)',
  receiptFallbackNote:
    'If this person is no longer a company admin when a gift goes out, the receipt goes to a Global ' +
    'Admin instead — it is never left undeliverable.',
};

/**
 * The console's headcount words where its build serves no Profile page (the page reveals under its
 * own knob): the prompt without a place, rather than a page that is not there.
 */
export const DONATIONS_WORDS_WITHOUT_PROFILE: EmployerModuleWords = {
  ...DONATIONS_WORDS,
  headcountPromptDashboard:
    'Add your employee headcount to see what share of your company has joined.',
  headcountPromptInvites:
    'Add your employee headcount to see what share of your company has joined.',
  headcountEditable: '',
  headcountAddWhere: 'Add your headcount to see a rate.',
};

/** The employer portal's addresses — the default, byte-identical to the pages before EP-MNT4. */
export const PORTAL_PATHS: EmployerModulePaths = {
  dashboard: '/dashboard',
  approvals: '/approvals',
  give: '/give',
  matchProgram: '/match-program',
  giftCards: '/gift-cards',
  employees: '/employees',
  competitions: '/competitions',
  competitionNew: '/competitions/new',
  competitionDetail: '/competitions/$competitionId',
  competitionEdit: '/competitions/$competitionId/edit',
  reports: '/reports',
  onboarding: '/onboarding/match-policy',
  paymentMethods: {
    to: '/settings',
    search: { tab: 'payments' },
    label: 'Settings → Payment Methods',
    goLabel: 'Go to Payment Methods',
  },
  joinRequests: {
    to: '/admins',
    label: 'Admin Team',
    detail: 'Review and assign their role on Admin Team.',
  },
  taxDocuments: null,
  privacy: null,
  aiRequests: null,
  words: PORTAL_WORDS,
};

/** Where the company console mounts the module (the registry's `employer` row, EP-MNT4). */
export const DONATIONS_PATH = '/donations';

/**
 * The company console's addresses: the module under `/donations`, and its three doors onto the
 * console's own Organization pages — the Wallet (`/wallet`), the join-request inbox
 * (`/access-requests`) and the tax documents (`/reports`).
 *
 * `as const`: the host registers its routes FROM these addresses, and a literal type is what lets the
 * router type a route's `$competitionId` param — a plain `string` path erases it.
 */
export const DONATIONS_PATHS = {
  dashboard: DONATIONS_PATH,
  approvals: `${DONATIONS_PATH}/approvals`,
  give: `${DONATIONS_PATH}/give`,
  matchProgram: `${DONATIONS_PATH}/match-program`,
  giftCards: `${DONATIONS_PATH}/gift-cards`,
  employees: `${DONATIONS_PATH}/employees`,
  competitions: `${DONATIONS_PATH}/competitions`,
  competitionNew: `${DONATIONS_PATH}/competitions/new`,
  competitionDetail: `${DONATIONS_PATH}/competitions/$competitionId`,
  competitionEdit: `${DONATIONS_PATH}/competitions/$competitionId/edit`,
  reports: `${DONATIONS_PATH}/reports`,
  onboarding: `${DONATIONS_PATH}/onboarding/match-policy`,
  paymentMethods: { to: '/wallet', label: 'Wallet', goLabel: 'Go to Wallet' },
  joinRequests: {
    to: '/access-requests',
    label: 'Join requests',
    detail: 'Review and assign their role in Join requests.',
  },
  taxDocuments: { to: '/reports', label: 'Open Tax documents' },
  // The composed Settings page, opened as from the Dashboard (the origin in its address, Org
  // Machinery OM-28: `from` the Dashboard's path, `frame` the module's slug), at the Company
  // visibility card. The console pins this against its own settings route and origin helper.
  privacy: {
    to: '/settings',
    search: { from: DONATIONS_PATH, frame: 'employer' },
    hash: 'privacy',
    label: 'Control your company’s visibility on Givmo',
  },
  // The console composes its own inbox door in (`apps/company`'s `donations-mount.tsx`): only it
  // knows its organization and whether its backend serves the inbox.
  aiRequests: null,
  words: DONATIONS_WORDS,
} as const satisfies EmployerModulePaths;

const EmployerModulePathsContext = createContext<EmployerModulePaths>(PORTAL_PATHS);

/** Hand the module this host's address book. Absent ⇒ the portal's. */
export function EmployerModulePathsProvider({
  paths,
  children,
}: {
  paths: EmployerModulePaths;
  children: ReactNode;
}) {
  return <EmployerModulePathsContext value={paths}>{children}</EmployerModulePathsContext>;
}

/** This host's address book — the portal's when no provider says otherwise. */
export function useEmployerModulePaths(): EmployerModulePaths {
  return use(EmployerModulePathsContext);
}
